import { FC, useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router';

import { useAuth } from 'contexts/auth';
import { useSelectedLocation } from 'contexts/selected-location';
import { getAuthenticatedUser } from 'services/users';
import { Role } from 'types/preferences';
import { useHistory } from './history';

type RequireRoleType = {
  allowCustomer?: boolean;
  allowSelf?: boolean;
  allowedRoles: Role[];
};

type RecoveryStatus = 'idle' | 'loading' | 'failed' | 'ok';

export const RequireRole: FC<RequireRoleType> = ({ allowCustomer = false, allowSelf = false, allowedRoles }) => {
  const { lastLocation } = useHistory();
  const { userProfile, setUserProfile } = useAuth();
  const { setSelectedLocation } = useSelectedLocation();
  const params = useParams();

  const needsRecovery = lastLocation === undefined && !!localStorage.getItem('token') && !userProfile.role;
  const [recovery, setRecovery] = useState<RecoveryStatus>(needsRecovery ? 'loading' : 'idle');

  useEffect(() => {
    if (!needsRecovery) return;
    let cancelled = false;
    getAuthenticatedUser().then(response => {
      if (cancelled) return;
      if (!response) {
        setRecovery('failed');
        return;
      }
      setUserProfile({
        username: response.username,
        email: response.email,
        role: response.role,
        active: response.active,
        customers: response.customers,
        truckingCompanies: response.truckingCompanies,
        locations: response.locations,
        defaultLocation: response.defaultLocation,
        columnPreferences: response.columnPreferences,
        savedFiltersJson: response.savedFiltersJson
      });
      if (response.defaultLocation) setSelectedLocation(response.defaultLocation);
      setRecovery('ok');
    });
    return () => {
      cancelled = true;
    };
  }, [needsRecovery, setUserProfile, setSelectedLocation]);

  if (allowSelf && userProfile?.username === params?.username) {
    return <Outlet />;
  } else if (userProfile && allowedRoles.find(role => role === userProfile.role)) {
    return <Outlet />;
  } else if (
    allowCustomer &&
    userProfile?.customers &&
    (userProfile.role === Role.ADMIN_CUSTOMER ||
      userProfile.role === Role.CUSTOMER ||
      userProfile.role === Role.EXTERNAL) &&
    userProfile.customers.some(({ id }) => `${id}` === params?.id)
  ) {
    return <Outlet />;
  }

  if (needsRecovery) {
    if (recovery === 'failed') {
      return (
        <Navigate
          to="/unauthorized"
          state={{ message: new Error('Your session has expired. Please sign in again.') }}
          replace
        />
      );
    }
    return null;
  }

  /* opened by link with no previous page: go to the role's own home, since '/' is the login page */
  return <Navigate to={lastLocation?.pathname ?? '/redirect'} state={{ message: new Error('Forbidden') }} />;
};
